import graphql from "babel-plugin-relay/macro";
import React, { useCallback, useState } from "react";
import { useFragment } from "react-relay";

import { MemberListModal } from "@/features/MemberListModal";
import { HomeHeader } from "@/screens/Home/components/HomeHeader/HomeHeader";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { SideNavBarFragment_session$key } from "./__generated__/SideNavBarFragment_session.graphql";
import { useGetSideNavBarItems } from "./hooks/useGetSideNavBarItems";

export const SideNavBar = ({
  sessionRef,
}: {
  sessionRef: SideNavBarFragment_session$key | null;
}) => {
  const session = useFragment(SideNavBarFragmentString, sessionRef);

  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);

  const openMemberModal = useCallback(() => {
    setIsMemberModalOpen(true);
  }, []);

  const closeMemberModal = useCallback(() => {
    setIsMemberModalOpen(false);
  }, []);

  const { items, links } = useGetSideNavBarItems({
    sessionRef: session ?? null,
    openMemberModal,
  });

  return (
    <Box gap={6} width={240} visible={{ base: false, lg: true }}>
      <Box width={240}>
        <HomeHeader sessionRef={session ?? null} showBookNowButton />
      </Box>
      <VStack gap={1} px={2}>
        {items.map((item) => (
          <Pressable
            key={item.label}
            onPress={item.routeHandler}
            paddingHorizontal={4}
            paddingVertical={3}
            borderRadius="2"
            backgroundColor={
              item.selected ? "surface-secondary" : "surface-primary"
            }
            _hover={{
              backgroundColor: "grayscale-200",
            }}
            _pressed={{
              backgroundColor: "grayscale-500",
            }}
            testID={
              item.routeName ? `tab-bar-item-${item.routeName}` : undefined
            }
          >
            <HStack gap={2}>
              <item.icon />
              <Text variant="mainSmallMedium">{item.label}</Text>
            </HStack>
          </Pressable>
        ))}
        {links.length > 0 ? (
          <Box paddingHorizontal={4} paddingVertical={3}>
            <Box
              width="100%"
              borderBottomWidth={1.5}
              borderColor="outline-default"
            />
          </Box>
        ) : null}
        {links?.map((link) =>
          link ? (
            <Pressable
              key={link.label}
              onPress={link.routeHandler}
              paddingHorizontal={4}
              paddingVertical={3}
              borderRadius="2"
              backgroundColor={
                link.selected ? "surface-secondary" : "surface-primary"
              }
              _hover={{
                backgroundColor: "grayscale-200",
              }}
              _pressed={{
                backgroundColor: "grayscale-500",
              }}
            >
              <HStack gap={2}>
                <link.icon />
                <Text variant="mainSmallMedium">{link.label}</Text>
              </HStack>
            </Pressable>
          ) : null
        )}
      </VStack>
      <MemberListModal isOpen={isMemberModalOpen} onClose={closeMemberModal} />
    </Box>
  );
};

export const SideNavBarFragmentString = graphql`
  fragment SideNavBarFragment_session on Session {
    ...HomeHeaderFragment_session
    ...useGetSideNavBarItemsFragment_session
  }
`;
